import React, { useState } from "react";
import { Container, Row, Col, Button, Dropdown} from "react-bootstrap";
import Modal from "../components/Modal";
import SlideIn from "../components/SlideIn";
import ContactForm from "../components/ContactForm";
import FeatureLanding from "../components/FeatureLanding";
import { Link } from "react-router-dom";
import api from "../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSmile, faPalette, faChartLine, faGlobe } from "@fortawesome/free-solid-svg-icons";

import { useTranslation } from "react-i18next";
import HeaderLanding from "../components/HeaderLanding";

function Home() {
  const { t } = useTranslation();  

  const [formData, setFormData] = useState({
    name: "",
    firstname: "",
    email: "",
    phone: "",
    comment: "",
    pro: false,
  });

  const [status, setStatus] = useState({ success: null, message: "" });
  const [isModalOpen, setModalOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ success: null, message: "" });

    try {
      await api.post("/contact_me", formData);
      setStatus({
        success: true,
        message: t("contact.success"),
      });

      setFormData({
        name: "",
        firstname: "",
        email: "",
        phone: "",
        comment: "",
        pro: false,
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = t("contact.error_default");

      if (typeof detail === "string") message = detail;
      else if (Array.isArray(detail)) message = detail.map((d) => d.msg).join("; ");

      setStatus({ success: false, message });
    }
  };

  return (
    <div className="">

      <HeaderLanding headerVariant="" />
      {/* HERO SECTION */}
      <section
        className="py-5 text-center text-white"
        style={{
          background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
          padding: "100px 20px 90px",
        }}
      >
        <Container>
          <h1 className="display-5 mb-3 fw-bold">
            {t("hero.title")}
          </h1>

          <p className="lead opacity-90">{t("hero.subtitle")}</p>

          <div className="mt-4">
            <Button as={Link} to="/login" size="lg" className="landing-page-btn">
              {t("hero.create_event")}
            </Button>
          </div>
        </Container>
      </section>

      {/* SOCIAL PROOF STRIP */}
      <section className="py-4 border-bottom" style={{ backgroundColor: "#ffffff" }}>
        <Container>
          <Row className="text-center gy-3">
            {["stat1", "stat2", "stat3", "stat4"].map((k) => (
              <Col xs={6} md={3} key={k}>
                <div className="fw-bold fs-3 lh-1">{t(`social_proof.${k}_number`)}</div>
                <div className="text-secondary small mt-1">{t(`social_proof.${k}_label`)}</div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ====================== */}
      {/*      FEATURES          */}
      {/* ====================== */}
      <section className="py-5" style={{ backgroundColor: "#ffffff" }}>
        <Container>
          <p className="text-center text-secondary text-uppercase fw-semibold small mb-5 ls-wide">
            {t("features.section_label")}
          </p>

          {/* Primary features — full alternating layout */}
          <FeatureLanding
            title={t("features.ai_title")}
            description={t("features.ai_desc")}
            img="/img/searchAI.gif"
            reverse={false}
          />
          <FeatureLanding
            title={t("features.event_gallery_title")}
            description={t("features.event_gallery_desc")}
            img="/img/gallery.gif"
            reverse={true}
          />
          <FeatureLanding
            title={t("features.upload_title")}
            description={t("features.upload_desc")}
            img="/img/upload.gif"
            reverse={false}
          />
          <FeatureLanding
            title={t("features.branding_title")}
            description={t("features.branding_desc")}
            img="/img/logoUrlsSettings.gif"
            reverse={true}
          />

          {/* Secondary features — compact icon grid */}
          <Row className="mt-5 pt-4 border-top gy-4 text-center">
            <Col md={4}>
              <FontAwesomeIcon icon={faChartLine} size="2x" className="mb-3" style={{ color: "#0f3460" }} />
              <h5 className="fw-semibold">{t("features.analytics_title")}</h5>
              <p className="text-secondary small">{t("features.analytics_desc")}</p>
            </Col>
            <Col md={4}>
              <FontAwesomeIcon icon={faSmile} size="2x" className="mb-3" style={{ color: "#0f3460" }} />
              <h5 className="fw-semibold">{t("features.qr_title")}</h5>
              <p className="text-secondary small">{t("features.qr_desc")}</p>
            </Col>
            <Col md={4}>
              <FontAwesomeIcon icon={faGlobe} size="2x" className="mb-3" style={{ color: "#0f3460" }} />
              <h5 className="fw-semibold">{t("features.boost_title")}</h5>
              <p className="text-secondary small">{t("features.boost_desc")}</p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CALL TO ACTION */}
      <section className="px-2 py-5 text-center mt-5" style={{ backgroundColor: "#1a1a2e" }}>
        <h2 className="fw-bold mb-3 text-white">
          {t("cta.ready_title")}
        </h2>
        <p className="fs-5 text-white opacity-75">{t("cta.ready_desc")}</p>
        <Button as={Link} to="/login" size="lg" className="landing-page-btn">
          {t("cta.start_btn")}
        </Button>
      </section>

      {/* CONTACT MODAL */}
      <Container className="text-center mt-5">
        <Modal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          title={t("contact.title")}
          bg="white"
        >
          <ContactForm
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            status={status}
          />
        </Modal>
      </Container>

      {/* FOOTER */}
      <footer className="py-4 bg-dark text-white mt-5">
        <Container>
          <div className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-2">
            <a href="https://www.pic-loud.com" className="d-flex align-items-center text-decoration-none">
              <img src="/logo_horizontal_white.png" alt="Pic-Loud" style={{ width: "auto", height: "20px"}} />
            </a>

            <nav className="d-flex align-items-center gap-3">
              <Link to="/privacy" className="text-white-50 text-decoration-none small fw-medium footer-link">Privacy Policy</Link>
              <span className="text-white-50 small">|</span>
              <Link to="/terms" className="text-white-50 text-decoration-none small fw-medium footer-link">Terms of Use</Link>
              <span className="text-white-50 small">|</span>
              <Button variant="link" className="text-white-50 text-decoration-none small fw-medium footer-link p-0" onClick={() => setModalOpen(true)}>
                {t("contact.btn")}
              </Button>
            </nav>

            <p className="text-white-50 small mb-0">
              &copy; {new Date().getFullYear()} Pic-Loud. All rights reserved.
            </p>
          </div>
        </Container>
      </footer>
    </div>
  );
}

export default Home;
